import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, Compass as CompassIcon } from "lucide-react";
import { useOnboarding } from "@/onboarding/context";

// STUDENT FRONT DOOR — the first thing a student sees. Orients a naïve user to what The Writing
// Studio is for, the two ways to enter, what each pathway includes, and that BOTH pathways converge
// at Composition. Student-facing terminology only (no OT / FSC / WSR / IST / AES). Routing is
// delegated to the parent via onChoose ("full" | "composition"); this screen builds no engines.

// Developmental sequence in student-facing language. `shared` steps are the common tail that BOTH
// pathways run through; `anchor` marks Composition — the recognizable convergence point.
const FULL_STEPS = [
  { label: "Thesis or Ideas" },
  { label: "Build" },
  { label: "Organize" },
  { label: "Write" },
  { label: "Strengthen", shared: true, anchor: true },
  { label: "Finish", shared: true },
];
const COMP_STEPS = [
  { label: "Your existing writing" },
  { label: "Strengthen", shared: true, anchor: true },
  { label: "Finish", shared: true },
];

// One step "chip". Shared steps are tinted terracotta (so the common tail looks identical in both
// pathways); Composition is the strongest (solid). Earlier full-path steps are neutral outlines.
function StepChip({ step, testid }) {
  const base = "inline-flex items-center rounded-full border px-3 py-1.5 text-[12px] leading-none whitespace-nowrap";
  const cls = step.anchor
    ? "bg-[#8C3A2A] border-[#8C3A2A] text-white font-semibold tracking-wide"
    : step.shared
    ? "bg-[#8C3A2A]/[0.08] border-[#8C3A2A]/40 text-[#8C3A2A] font-medium"
    : "bg-white border-stone-300 text-stone-700";
  return <span data-testid={testid} className={`${base} ${cls}`}>{step.label}</span>;
}

function StepFlow({ steps, idPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2" data-testid={`${idPrefix}-flow`}>
      {steps.map((s, i) => (
        <span key={s.label} className="inline-flex items-center gap-x-1.5">
          <StepChip step={s} testid={`${idPrefix}-step-${i}`} />
          {i < steps.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-stone-300 shrink-0" aria-hidden="true" />}
        </span>
      ))}
    </div>
  );
}

function PathwayCard({ testid, kicker, title, subtitle, goodWhen, steps, idPrefix, cta, onStart, delay, secondary }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay }}
      className="flex flex-col rounded-xl border border-stone-200 bg-white p-6 sm:p-7 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
    >
      <p className="font-mono-panel text-[10px] uppercase tracking-[0.2em] text-[#8C3A2A]">{kicker}</p>
      <h2 className="font-serif-display text-2xl sm:text-[1.7rem] text-stone-900 mt-1.5 leading-tight">{title}</h2>
      <p className="text-[14px] text-stone-600 mt-1.5">{subtitle}</p>

      <div className="mt-5">
        <StepFlow steps={steps} idPrefix={idPrefix} />
      </div>

      <div className="mt-5 flex items-start gap-2 text-[13px] text-stone-500">
        <span className="font-mono-panel text-[9.5px] uppercase tracking-[0.16em] text-stone-400 mt-0.5 shrink-0">Good when</span>
        <span className="leading-snug">{goodWhen}</span>
      </div>

      <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
        <button
          onClick={onStart}
          data-testid={`${idPrefix}-start`}
          className="group inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] hover:-translate-y-px transition-[background-color,transform] focus:outline-none focus:ring-2 focus:ring-[#8C3A2A] focus:ring-offset-2"
        >
          {cta}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        {secondary}
      </div>
    </motion.div>
  );
}

export default function StudentPathway({ onChoose }) {
  const onboarding = useOnboarding();
  return (
    <div className="min-h-screen paper-grain text-stone-900" data-testid="student-pathway">
      <div className="mx-auto w-full max-w-5xl px-6 sm:px-8 py-14 sm:py-20">
        {/* Identity + orienting statement */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <a href="/" data-testid="pathway-home-link" title="Home" className="inline-flex items-center gap-2 text-stone-400 hover:text-[#8C3A2A] font-mono-panel text-[10px] uppercase tracking-[0.2em] mb-4 transition-colors">
            <CompassIcon className="h-3.5 w-3.5" />
            The Writing Studio
          </a>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-stone-900">
            Develop your thinking.
            <span className="block text-[#8C3A2A]">Develop your writing.</span>
          </h1>
          <p className="text-[16px] text-stone-600 leading-relaxed mt-6 max-w-2xl">
            The Writing Studio helps you do the intellectual work of writing yourself. You can work from your
            first understanding of an assignment all the way through a finished piece, or begin with writing
            you&rsquo;re already ready to develop.
          </p>
        </motion.div>

        {/* Primary question */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 font-mono-panel text-[11px] uppercase tracking-[0.22em] text-stone-500"
        >
          How would you like to work?
        </motion.h2>

        {/* Two pathways */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-stretch">
          <PathwayCard
            testid="pathway-full"
            idPrefix="pathway-full"
            kicker="Full Writing Studio"
            title="Develop your ideas and your writing from beginning to end"
            subtitle="Thesis or ideas first; the whole process from there."
            goodWhen="You're still working out what you want to say."
            steps={FULL_STEPS}
            cta="Start the Full Path"
            onStart={() => onChoose("full")}
            delay={0.16}
          />
          <PathwayCard
            testid="pathway-composition"
            idPrefix="pathway-composition"
            kicker="Strengthen My Writing"
            title="Already have something written?"
            subtitle="Type in anything from a paragraph to a full paper and make it stronger."
            goodWhen="You substantially know what you are saying and want to improve the writing you already have."
            steps={COMP_STEPS}
            cta="Strengthen my writing"
            onStart={() => onChoose("composition")}
            delay={0.22}
            secondary={onboarding ? <button type="button" data-testid="pathway-strengthen-tour" onClick={() => onboarding.startStrengthen()} className="text-[12px] text-stone-500 underline decoration-stone-300 underline-offset-4 hover:text-[#8C3A2A]">See how it works — 2 min</button> : null}
          />
        </div>

        {/* Convergence — both pathways meet at Composition and share the same tail. */}
        <motion.div
          data-testid="pathway-convergence"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.3 }}
          className="mt-10 rounded-xl border border-stone-200 bg-stone-50/70 p-6 sm:p-7"
        >
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-4">
            Both doors lead to Strengthen and Finish
          </p>
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
            {/* Two entry points */}
            <div className="flex flex-col gap-2">
              <span className="inline-flex items-center rounded-md border border-stone-300 bg-white px-3 py-2 text-[12px] text-stone-600">
                <span className="font-medium text-stone-800 mr-1.5">Full path</span> Understand · Organize · Build
              </span>
              <span className="inline-flex items-center rounded-md border border-stone-300 bg-white px-3 py-2 text-[12px] text-stone-600">
                <span className="font-medium text-stone-800 mr-1.5">Strengthen My Writing</span> starts here
              </span>
            </div>
            {/* Merge arrow — points down on mobile, right on desktop */}
            <div className="flex items-center justify-center text-stone-400" aria-hidden="true">
              <ArrowRight className="hidden md:block h-5 w-5" />
              <ArrowRight className="md:hidden h-5 w-5 rotate-90" />
            </div>
            {/* Shared spine */}
            <div className="flex-1 rounded-lg border border-[#8C3A2A]/25 bg-white p-3">
              <StepFlow steps={COMP_STEPS} idPrefix="convergence" />
            </div>
          </div>
          <p className="text-[12.5px] text-stone-500 mt-4 leading-snug">
            Full Writing Studio develops the thinking and the writing from the beginning. Strengthen My Writing is a different door, for writing that already exists: it joins at Strengthen &mdash; and both
            continue together through Sentence Craft and Proofreading.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
